/**
 * Adyen `resultCode` → app status route mapper, plus the per-country
 * terms / privacy / subscription URL lookups.
 *
 * Ported from `redirect.js` with `as const` so TS narrows the country-keyed
 * lookups and so a misspelled key fails at compile time.
 */

import type { AdyenPaymentCompletedResult } from '../types/adyen';

/**
 * Maps an Adyen `resultCode` to a React Router path inside the status
 * surface. Any unknown code falls through to `/status/error`.
 */
export function getRedirectUrl(resultCode: AdyenPaymentCompletedResult['resultCode']): string {
  switch (resultCode) {
    case 'Authorised':
      return '/status/success';
    case 'Pending':
    case 'Received':
      return '/status/pending';
    case 'Refused':
    case 'Cancelled':
      return '/status/failed';
    default:
      return '/status/error';
  }
}

export const getTermsAndConditionsByCountry = {
  NZ: 'https://www.fisherpaykel.com/nz/terms-and-conditions',
  AU: 'https://www.fisherpaykel.com/au/terms-and-conditions',
  GB: 'https://www.fisherpaykel.com/uk/terms-and-conditions',
  US: 'https://www.fisherpaykel.com/us/terms-and-conditions',
  CA: 'https://www.fisherpaykel.com/ca/terms-and-conditions',
  CA_FR: 'https://www.fisherpaykel.com/ca-fr/terms-and-conditions',
} as const;

export const getPrivacyAndCollectionByCountry = {
  NZ: 'https://www.fisherpaykel.com/nz/privacy-policy',
  AU: 'https://www.fisherpaykel.com/au/privacy-policy',
  GB: 'https://www.fisherpaykel.com/uk/privacy-policy',
  US: 'https://www.fisherpaykel.com/us/privacy-policy',
  CA: 'https://www.fisherpaykel.com/ca/privacy-policy',
  CA_FR: 'https://www.fisherpaykel.com/ca-fr/privacy-policy',
} as const;

export const getSubscriptionTermsByCountry = {
  NZ: 'https://www.fisherpaykel.com/nz/terms-and-conditions#Subscriptions',
  AU: 'https://www.fisherpaykel.com/au/terms-and-conditions#Subscriptions',
  GB: 'https://www.fisherpaykel.com/uk/terms-and-conditions#Subscriptions',
  US: 'https://www.fisherpaykel.com/us/terms-and-conditions#Subscriptions',
  CA: 'https://www.fisherpaykel.com/ca/terms-and-conditions#Subscriptions',
  CA_FR: 'https://www.fisherpaykel.com/ca-fr/terms-and-conditions#Subscriptions',
} as const;

/** Safe accessor with US fallback (mirrors the original `getSubscriptionTermsUrl` helper). */
export function getSubscriptionTermsUrl(country: string | null | undefined): string {
  const key = (country?.toUpperCase() ?? '') as keyof typeof getSubscriptionTermsByCountry;
  return getSubscriptionTermsByCountry[key] ?? getSubscriptionTermsByCountry.US;
}
